import { useEffect, useState } from "react";
import {
  Banner,
  Button,
  Field,
  Panel,
  PanelGrid,
  ProgressBar,
  Spinner,
  TextArea,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { mailingApi, type MailingSettingsDto } from "../../api/mailingApi";
import { useShell } from "../shell/ShellContext";
import { errorMessage, fmtNum } from "./mailingUi";

/** Sender name, reply-to and the organization address of the legal footer; the month's quota. */
export function MailingSettingsPanel() {
  const toast = useCkToast();
  const { allowed } = useShell();
  const canManage = allowed("mailing.manage");
  const [s, setS] = useState<MailingSettingsDto | null>(null);
  const [form, setForm] = useState({ senderName: "", replyTo: "", footerAddress: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    mailingApi
      .settings()
      .then((x) => {
        setS(x);
        setForm({ senderName: x.senderName, replyTo: x.replyTo, footerAddress: x.footerAddress });
      })
      .catch((e) => setError(errorMessage(e, "לא הצלחנו לטעון את ההגדרות")));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const x = await mailingApi.saveSettings(form);
      setS(x);
      toast.success("ההגדרות נשמרו");
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Banner tone="danger">{error}</Banner>;
  if (!s) {
    return (
      <div className="ml-busy">
        <Spinner size={24} />
      </div>
    );
  }
  const q = s.quota;
  const used = Math.min(100, Math.round(((q.used + q.pending) / Math.max(1, q.quota)) * 100));
  return (
    <PanelGrid cols={2}>
      <Panel title="פרטי השולח" icon="mail">
        <div className="ml-stack">
          <Field label="שם השולח" hint="מופיע בתיבת הדואר של הנמען. ברירת מחדל: שם הארגון.">
            <TextInput value={form.senderName} onChange={(e) => setForm({ ...form, senderName: e.target.value })} disabled={!canManage} maxLength={120} />
          </Field>
          <Field label="כתובת למענה" hint="כשנמען עונה למייל — התשובה מגיעה לכאן.">
            <TextInput ltr type="email" value={form.replyTo} onChange={(e) => setForm({ ...form, replyTo: e.target.value })} disabled={!canManage} />
          </Field>
          <Field label="כתובת הארגון (לתחתית המייל)" hint="חובה לדבר פרסומת לפי חוק התקשורת: שם וכתובת של השולח. למשל: רח' הרב קוק 5, ירושלים · 02-1234567">
            <TextArea rows={2} value={form.footerAddress} onChange={(e) => setForm({ ...form, footerAddress: e.target.value })} disabled={!canManage} maxLength={300} />
          </Field>
          {canManage ? (
            <div className="ml-row ml-row--end">
              <Button variant="primary" icon="check" onClick={() => void save()} disabled={busy}>
                {busy ? "שומר…" : "שמירה"}
              </Button>
            </div>
          ) : null}
          <p className="ml-note">
            המיילים יוצאים מהכתובת המאומתת של המערכת, עם שם השולח שלכם. תשובות מגיעות לכתובת למענה.
          </p>
        </div>
      </Panel>
      <Panel title="מכסה חודשית" icon="chart">
        <div className="ml-stack">
          {q.suspended ? <Banner tone="danger">השליחה מושעית{q.suspendedReason ? `: ${q.suspendedReason}` : ""}</Banner> : null}
          <ProgressBar value={used} tone={used >= 90 ? "danger" : used >= 70 ? "warning" : "primary"} label={`${fmtNum(q.used + q.pending)} מתוך ${fmtNum(q.quota)}`} />
          <ul className="ml-aud__rows">
            <li>
              <span>נשלחו החודש</span>
              <span>{fmtNum(q.used)}</span>
            </li>
            <li>
              <span>ממתינים בתור</span>
              <span>{fmtNum(q.pending)}</span>
            </li>
            <li>
              <span>נותרו</span>
              <span>{fmtNum(q.remaining)}</span>
            </li>
          </ul>
          <p className="ml-note">
            {q.allowOverage ? "מותרת חריגה מהמכסה." : "כשהמכסה נגמרת, השליחה נעצרת ומחכה לחודש הבא."} להגדלת המכסה — פנו לתמיכה. ארגון עם הרבה
            כתובות שחוזרות או דיווחי ספאם מושעה אוטומטית, כדי לשמור על יכולת השליחה של כולם.
          </p>
        </div>
      </Panel>
    </PanelGrid>
  );
}
